'use client';
import React, { useState } from 'react';
import {
  Shield, Upload, Camera, Users, ClipboardCheck, ChevronRight,
  CheckCircle, AlertTriangle, FileText, Trash2, Eye, RefreshCw,
  Plus, X, Info, Lock, UserCheck, Building2, Flag, ArrowRight,
  Clock, AlertCircle, Download
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import MetricCard from '@/components/ui/MetricCard';

// ─── Types ────────────────────────────────────────────────────────────────────
type ApplicationType = 'individual' | 'corporate';
type StepId = 'applicant' | 'documents' | 'liveness' | 'ubo' | 'review';

interface UploadedDoc {
  id: string;
  name: string;
  type: string;
  size: string;
  status: 'uploaded' | 'scanning' | 'verified' | 'rejected';
  uploadedAt: string;
}

interface UBOEntry {
  id: string;
  name: string;
  nationality: string;
  dob: string;
  ownership: string;
  isPEP: boolean;
  docStatus: 'pending' | 'uploaded' | 'verified';
}

// ─── Step Config ──────────────────────────────────────────────────────────────
const STEPS: { id: StepId; label: string; icon: React.ElementType }[] = [
  { id: 'applicant', label: 'Applicant Info', icon: UserCheck },
  { id: 'documents', label: 'Identity Documents', icon: FileText },
  { id: 'liveness', label: 'Liveness Check', icon: Camera },
  { id: 'ubo', label: 'UBO Declarations', icon: Users },
  { id: 'review', label: 'Compliance Review', icon: ClipboardCheck },
];

// ─── Mock queue data ──────────────────────────────────────────────────────────
const COMPLIANCE_OFFICERS = [
  'L. Okafor — Senior Compliance Officer',
  'M. Patel — KYC Analyst',
  'S. Bergmann — AML Specialist',
  'R. Tanaka — EDD Officer',
  'Unassigned',
];

const REVIEW_QUEUES = [
  { id: 'q-standard', label: 'Standard KYC Queue', sla: '48h', count: 14 },
  { id: 'q-edd', label: 'Enhanced Due Diligence (EDD)', sla: '5 days', count: 5 },
  { id: 'q-pep', label: 'PEP / Sanctions Review', sla: '24h', count: 3 },
  { id: 'q-corporate', label: 'Corporate KYB Queue', sla: '72h', count: 8 },
  { id: 'q-urgent', label: 'Urgent / Escalated', sla: '4h', count: 2 },
];

const INITIAL_UBOS: UBOEntry[] = [
  { id: 'ubo-1', name: '', nationality: '', dob: '', ownership: '', isPEP: false, docStatus: 'pending' },
];

// ─── Document Upload Row ──────────────────────────────────────────────────────
function DocRow({ doc, onRemove }: { doc: UploadedDoc; onRemove: (id: string) => void }) {
  const statusConfig: Record<UploadedDoc['status'], { label: string; variant: 'active' | 'pending' | 'review' | 'rejected'; icon: React.ElementType }> = {
    uploaded: { label: 'Uploaded', variant: 'pending', icon: Clock },
    scanning: { label: 'Scanning…', variant: 'review', icon: RefreshCw },
    verified: { label: 'Verified', variant: 'active', icon: CheckCircle },
    rejected: { label: 'Rejected', variant: 'rejected', icon: AlertTriangle },
  };
  const cfg = statusConfig[doc.status];
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-3 border border-border group">
      <div className="w-8 h-8 rounded-md bg-surface-4 flex items-center justify-center flex-shrink-0">
        <FileText size={14} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
        <p className="text-xs text-muted-foreground">{doc.type} · {doc.size} · {doc.uploadedAt}</p>
      </div>
      <StatusBadge variant={cfg.variant} label={cfg.label} dot />
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button className="p-1.5 rounded hover:bg-surface-4 text-muted-foreground hover:text-foreground transition-colors">
          <Eye size={13} />
        </button>
        <button
          onClick={() => onRemove(doc.id)}
          className="p-1.5 rounded hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Upload Zone ──────────────────────────────────────────────────────────────
function UploadZone({ label, hint, onUpload }: { label: string; hint: string; onUpload: () => void }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); onUpload(); }}
      onClick={onUpload}
      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
        dragging
          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-surface-3'
      }`}
    >
      <Upload size={22} className="mx-auto mb-2 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}

// ─── Liveness Panel ──────────────────────────────────────────────────────────
function LivenessPanel({ status, onTrigger }: { status: 'idle' | 'pending' | 'passed' | 'failed'; onTrigger: () => void }) {
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-xl p-5 flex flex-col items-center gap-4 text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center border-2 transition-all ${
          status === 'passed' ? 'border-positive bg-positive/10' :
          status === 'failed' ? 'border-negative bg-negative/10' :
          status === 'pending'? 'border-warning bg-warning/10 animate-pulse' : 'border-border bg-surface-3'
        }`}>
          <Camera size={32} className={
            status === 'passed' ? 'text-positive' :
            status === 'failed' ? 'text-negative' :
            status === 'pending'? 'text-warning' : 'text-muted-foreground'
          } />
        </div>
        <div>
          <p className="font-semibold text-foreground">
            {status === 'idle' && 'Liveness Verification Required'}
            {status === 'pending' && 'Verification In Progress…'}
            {status === 'passed' && 'Liveness Check Passed'}
            {status === 'failed' && 'Liveness Check Failed'}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
            {status === 'idle' && 'Initiate a real-time biometric liveness check via the eKYC provider hook. The applicant will complete a selfie + challenge sequence.'}
            {status === 'pending' && 'Awaiting applicant to complete the biometric challenge. Session expires in 15 minutes.'}
            {status === 'passed' && 'Biometric match confirmed. Confidence score: 98.4%. Selfie matched to passport photo.'}
            {status === 'failed' && 'Biometric challenge failed. Possible spoof attempt detected. Manual review required.'}
          </p>
        </div>
        {(status === 'idle' || status === 'failed') && (
          <button onClick={onTrigger} className="btn-primary text-sm gap-2">
            <Camera size={14} />
            {status === 'failed' ? 'Retry Liveness Check' : 'Initiate Liveness Check'}
          </button>
        )}
        {status === 'pending' && (
          <div className="flex items-center gap-2 text-warning text-sm">
            <RefreshCw size={14} className="animate-spin" />
            <span>Waiting for applicant response…</span>
          </div>
        )}
        {status === 'passed' && (
          <div className="flex items-center gap-2 text-positive text-sm font-medium">
            <CheckCircle size={14} />
            <span>Verified — 20 Aug 2026, 15:44 UTC</span>
          </div>
        )}
      </div>

      {/* eKYC Provider Hook Info */}
      <div className="rounded-lg bg-info/5 border border-info/20 p-4 flex gap-3">
        <Info size={15} className="text-info flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-info mb-1">eKYC Provider Integration Hook</p>
          <p className="text-xs text-muted-foreground">
            This step dispatches a webhook to the configured eKYC provider (e.g. Onfido, Jumio, Veriff). The provider session URL is returned and sent to the applicant via email/SMS. Results are received via signed callback and stored in the document vault.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {['Onfido', 'Jumio', 'Veriff', 'iProov', 'Sumsub'].map(p => (
              <span key={p} className="text-xs px-2 py-0.5 rounded bg-surface-3 border border-border text-muted-foreground font-mono-data">{p}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Liveness session log */}
      <div className="rounded-xl bg-surface-2 border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Session Log</p>
        </div>
        <div className="divide-y divide-border">
          {[
            { time: '16:32:01', event: 'Liveness session initiated', actor: 'System' },
            { time: '16:32:03', event: 'eKYC webhook dispatched to provider', actor: 'System' },
            { time: '16:32:05', event: 'Session URL delivered to applicant via email', actor: 'System' },
          ].map((log, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <span className="font-mono-data text-xs text-muted-foreground w-16 flex-shrink-0">{log.time}</span>
              <span className="text-xs text-foreground flex-1">{log.event}</span>
              <span className="text-xs text-muted-foreground">{log.actor}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── UBO Declaration Row ──────────────────────────────────────────────────────
function UBORow({ ubo, index, onChange, onRemove }: {
  ubo: UBOEntry; index: number;
  onChange: (id: string, field: keyof UBOEntry, value: string | boolean) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-surface-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">{index + 1}</span>
          </div>
          <span className="text-sm font-semibold text-foreground">
            {ubo.name || `Ultimate Beneficial Owner ${index + 1}`}
          </span>
          {ubo.isPEP && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning border border-warning/30 font-medium">PEP</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge
            variant={ubo.docStatus === 'verified' ? 'active' : ubo.docStatus === 'uploaded' ? 'pending' : 'review'}
            label={ubo.docStatus === 'verified' ? 'Docs Verified' : ubo.docStatus === 'uploaded' ? 'Docs Uploaded' : 'Docs Pending'}
            dot
          />
          {index > 0 && (
            <button onClick={() => onRemove(ubo.id)} className="p-1.5 rounded hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors">
              <X size={13} />
            </button>
          )}
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Full Legal Name</label>
          <input
            type="text"
            value={ubo.name}
            onChange={(e) => onChange(ubo.id, 'name', e.target.value)}
            placeholder="As per passport / ID"
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Nationality</label>
          <input
            type="text"
            value={ubo.nationality}
            onChange={(e) => onChange(ubo.id, 'nationality', e.target.value)}
            placeholder="Country of citizenship"
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Date of Birth</label>
          <input
            type="date"
            value={ubo.dob}
            onChange={(e) => onChange(ubo.id, 'dob', e.target.value)}
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Ownership / Control %</label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              value={ubo.ownership}
              onChange={(e) => onChange(ubo.id, 'ownership', e.target.value)}
              placeholder="e.g. 25"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 pr-8 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors font-mono-data"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
          </div>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div
              onClick={() => onChange(ubo.id, 'isPEP', !ubo.isPEP)}
              className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${ubo.isPEP ? 'bg-warning' : 'bg-surface-4'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${ubo.isPEP ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Politically Exposed Person</p>
              <p className="text-xs text-muted-foreground">PEP or close associate</p>
            </div>
          </label>
        </div>
        <div className="flex items-end">
          <button className="btn-secondary text-xs gap-1.5 w-full justify-center">
            <Upload size={12} />
            Upload UBO Documents
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
export default function KYCIntakeScreen() {
  const [appType, setAppType] = useState<ApplicationType>('individual');
  const [currentStep, setCurrentStep] = useState<StepId>('applicant');
  const [livenessStatus, setLivenessStatus] = useState<'idle' | 'pending' | 'passed' | 'failed'>('idle');
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([
    { id: 'd-1', name: 'passport_john_doe.pdf', type: 'Passport', size: '2.4 MB', status: 'verified', uploadedAt: '20 Aug 2026' },
    { id: 'd-2', name: 'utility_bill_aug2026.pdf', type: 'Proof of Address', size: '1.1 MB', status: 'scanning', uploadedAt: '20 Aug 2026' },
  ]);
  const [ubos, setUBOs] = useState<UBOEntry[]>(INITIAL_UBOS);
  const [selectedQueue, setSelectedQueue] = useState('q-standard');
  const [selectedOfficer, setSelectedOfficer] = useState(COMPLIANCE_OFFICERS[0]);
  const [riskLevel, setRiskLevel] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);

  const handleAddDoc = () => {
    const newDoc: UploadedDoc = {
      id: `d-${Date.now()}`,
      name: `document_${uploadedDocs.length + 1}.pdf`,
      type: 'Supporting Document',
      size: `${(Math.random() * 3 + 0.5).toFixed(1)} MB`,
      status: 'uploaded',
      uploadedAt: '20 Aug 2026',
    };
    setUploadedDocs(prev => [...prev, newDoc]);
  };

  const handleRemoveDoc = (id: string) => {
    setUploadedDocs(prev => prev.filter(d => d.id !== id));
  };

  const handleAddUBO = () => {
    setUBOs(prev => [...prev, { id: `ubo-${Date.now()}`, name: '', nationality: '', dob: '', ownership: '', isPEP: false, docStatus: 'pending' }]);
  };

  const handleRemoveUBO = (id: string) => {
    setUBOs(prev => prev.filter(u => u.id !== id));
  };

  const handleUBOChange = (id: string, field: keyof UBOEntry, value: string | boolean) => {
    setUBOs(prev => prev.map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  const handleLiveness = () => {
    setLivenessStatus('pending');
    setTimeout(() => setLivenessStatus('passed'), 3000);
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
        <div className="w-20 h-20 rounded-full bg-positive/10 border-2 border-positive flex items-center justify-center">
          <CheckCircle size={36} className="text-positive" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Application Submitted</h2>
          <p className="text-muted-foreground mt-2 max-w-md">
            KYC/KYB intake case <span className="font-mono-data text-primary">KYC-2026-{Math.floor(Math.random() * 9000 + 1000)}</span> has been created and assigned to the <strong>{REVIEW_QUEUES.find(q => q.id === selectedQueue)?.label}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSubmitted(false)} className="btn-secondary text-sm gap-2">
            <Plus size={14} /> New Application
          </button>
          <button className="btn-primary text-sm gap-2">
            <ArrowRight size={14} /> Go to Compliance Queue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">KYC / KYB Intake</h1>
          <p className="text-sm text-muted-foreground mt-1">Identity verification · Document upload · Liveness check · UBO declarations · Compliance queue assignment</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><Download size={12} /> Export Draft</button>
          <button className="btn-secondary text-xs gap-1.5"><Lock size={12} /> Save & Hold</button>
        </div>
      </div>

      {/* Application type selector */}
      <div className="glass-card rounded-xl p-4 flex flex-col sm:flex-row gap-3">
        <p className="text-sm font-semibold text-muted-foreground self-center mr-2 flex-shrink-0">Application Type:</p>
        <div className="flex gap-2 flex-wrap">
          {([
            { id: 'individual', label: 'Individual (KYC)', icon: UserCheck, desc: 'Natural person investor or director' },
            { id: 'corporate', label: 'Corporate / Entity (KYB)', icon: Building2, desc: 'Company, fund, trust or institution' },
          ] as const).map(opt => (
            <button
              key={opt.id}
              onClick={() => setAppType(opt.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                appType === opt.id
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-surface-3 text-muted-foreground hover:border-border/80 hover:text-foreground'
              }`}
            >
              <opt.icon size={18} className={appType === opt.id ? 'text-primary' : ''} />
              <div>
                <p className="text-sm font-semibold">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Step progress */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center gap-0 overflow-x-auto">
          {STEPS.map((step, i) => {
            const isActive = step.id === currentStep;
            const isDone = i < stepIndex;
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all flex-shrink-0 ${
                    isActive ? 'bg-primary/10 text-primary' : isDone ?'text-positive hover:bg-surface-3': 'text-muted-foreground hover:bg-surface-3 hover:text-foreground'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${
                    isActive ? 'border-primary bg-primary text-white' : isDone ?'border-positive bg-positive/10 text-positive': 'border-border bg-surface-3'
                  }`}>
                    {isDone ? <CheckCircle size={12} /> : i + 1}
                  </div>
                  <span className="text-xs font-semibold whitespace-nowrap">{step.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <ChevronRight size={14} className="text-border flex-shrink-0 mx-1" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Step: Applicant Info ─────────────────────────────────────────────── */}
      {currentStep === 'applicant' && (
        <div className="space-y-5">
          <SectionHeader title="Applicant Information" subtitle={appType === 'individual' ? 'Personal details for individual KYC' : 'Entity details for corporate KYB'} />
          <div className="glass-card rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {appType === 'individual' ? (
              <>
                {[
                  { label: 'First Name', placeholder: 'Legal first name' },
                  { label: 'Last Name', placeholder: 'Legal last name' },
                  { label: 'Date of Birth', placeholder: '', type: 'date' },
                  { label: 'Nationality', placeholder: 'Country of citizenship' },
                  { label: 'Country of Residence', placeholder: 'Current country of residence' },
                  { label: 'National ID / Passport Number', placeholder: 'Document number' },
                  { label: 'Tax Identification Number (TIN)', placeholder: 'TIN / SSN / NIN' },
                  { label: 'Email Address', placeholder: 'Applicant email', type: 'email' },
                  { label: 'Phone Number', placeholder: '+1 555 000 0000', type: 'tel' },
                  { label: 'Source of Funds', placeholder: 'Employment, business, inheritance…' },
                ].map(field => (
                  <div key={field.label}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
                    />
                  </div>
                ))}
              </>
            ) : (
              <>
                {[
                  { label: 'Legal Entity Name', placeholder: 'Full registered name' },
                  { label: 'Registration Number', placeholder: 'Company / entity registration number' },
                  { label: 'Jurisdiction of Incorporation', placeholder: 'Country / state' },
                  { label: 'Date of Incorporation', placeholder: '', type: 'date' },
                  { label: 'Entity Type', placeholder: 'Ltd, LLC, Trust, Fund, Partnership…' },
                  { label: 'Registered Address', placeholder: 'Full registered address' },
                  { label: 'Principal Business Activity', placeholder: 'Describe primary business' },
                  { label: 'Tax Identification Number (TIN)', placeholder: 'Corporate TIN / EIN' },
                  { label: 'LEI (if applicable)', placeholder: '20-character LEI code' },
                  { label: 'Contact Email', placeholder: 'Primary contact email', type: 'email' },
                ].map(field => (
                  <div key={field.label}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          {/* FATCA / CRS / Suitability flags */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm font-semibold text-foreground">Regulatory Declarations</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'FATCA — US Person', desc: 'US citizen, resident or entity' },
                { label: 'CRS — Reportable Jurisdiction', desc: 'Tax resident in CRS-participating country' },
                { label: 'PEP Declaration', desc: 'Politically exposed person or close associate' },
                { label: 'Sanctions Self-Declaration', desc: 'Not subject to any sanctions regime' },
              ].map(item => (
                <label key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-surface-3 border border-border cursor-pointer hover:border-primary/30 transition-colors">
                  <input type="checkbox" className="mt-0.5 accent-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Step: Identity Documents ─────────────────────────────────────────── */}
      {currentStep === 'documents' && (
        <div className="space-y-5">
          <SectionHeader title="Identity Document Upload" subtitle="Upload certified copies of required identity and supporting documents" />

          {/* Required docs checklist */}
          <div className="glass-card rounded-xl p-5">
            <p className="text-sm font-semibold text-foreground mb-3">Required Documents</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(appType === 'individual' ? [
                { label: 'Government-issued Photo ID', required: true, done: true },
                { label: 'Proof of Address (< 3 months)', required: true, done: false },
                { label: 'Selfie with ID', required: true, done: false },
                { label: 'Source of Funds Evidence', required: false, done: false },
                { label: 'Bank Statement (3 months)', required: false, done: false },
                { label: 'Tax Residency Certificate', required: false, done: false },
              ] : [
                { label: 'Certificate of Incorporation', required: true, done: true },
                { label: 'Memorandum & Articles', required: true, done: false },
                { label: 'Register of Directors', required: true, done: false },
                { label: 'Register of Shareholders', required: true, done: true },
                { label: 'Audited Financial Statements', required: false, done: false },
                { label: 'Bank Reference Letter', required: false, done: false },
                { label: 'Proof of Registered Address', required: true, done: false },
                { label: 'Board Resolution', required: false, done: false },
              ]).map(doc => (
                <div key={doc.label} className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${doc.done ? 'border-positive/30 bg-positive/5' : 'border-border bg-surface-3'}`}>
                  {doc.done
                    ? <CheckCircle size={14} className="text-positive flex-shrink-0" />
                    : <AlertCircle size={14} className={`flex-shrink-0 ${doc.required ? 'text-warning' : 'text-muted-foreground'}`} />
                  }
                  <span className="text-xs text-foreground">{doc.label}</span>
                  {doc.required && !doc.done && (
                    <span className="ml-auto text-xs text-warning font-medium flex-shrink-0">Required</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Upload zones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UploadZone
              label="Primary Identity Document"
              hint="Passport, National ID, Driver's Licence — PDF, JPG, PNG up to 10MB"
              onUpload={handleAddDoc}
            />
            <UploadZone
              label="Proof of Address"
              hint="Utility bill, bank statement, government letter — dated within 3 months"
              onUpload={handleAddDoc}
            />
            <UploadZone
              label="Supporting Documents"
              hint="Source of funds, bank statements, tax certificates, board resolutions"
              onUpload={handleAddDoc}
            />
            <UploadZone
              label="Additional Documents"
              hint="Any other documents required for compliance review"
              onUpload={handleAddDoc}
            />
          </div>

          {/* Uploaded files */}
          {uploadedDocs.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Uploaded Files ({uploadedDocs.length})</p>
              {uploadedDocs.map(doc => (
                <DocRow key={doc.id} doc={doc} onRemove={handleRemoveDoc} />
              ))}
            </div>
          )}

          {/* Security notice */}
          <div className="rounded-lg bg-surface-3 border border-border p-4 flex gap-3">
            <Lock size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              All documents are encrypted at rest (AES-256) and stored in the private document vault with access logging. Malware scanning is performed on every upload. Documents are accessible only to authorised compliance personnel via signed URLs.
            </p>
          </div>
        </div>
      )}

      {/* ── Step: Liveness Verification ──────────────────────────────────────── */}
      {currentStep === 'liveness' && (
        <div className="space-y-5">
          <SectionHeader title="Liveness Verification" subtitle="Real-time biometric check via eKYC provider integration hook" />
          <LivenessPanel status={livenessStatus} onTrigger={handleLiveness} />
        </div>
      )}

      {/* ── Step: UBO Declarations ───────────────────────────────────────────── */}
      {currentStep === 'ubo' && (
        <div className="space-y-5">
          <SectionHeader
            title="Ultimate Beneficial Owner (UBO) Declarations"
            subtitle={appType === 'individual' ? 'Declare any third parties with beneficial interest in this investment' : 'Declare all natural persons owning or controlling ≥25% of the entity'}
          />

          {/* UBO threshold notice */}
          <div className="rounded-lg bg-warning/5 border border-warning/20 p-4 flex gap-3">
            <AlertTriangle size={15} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-warning mb-1">Regulatory Requirement</p>
              <p className="text-xs text-muted-foreground">
                {appType === 'corporate' ?'All natural persons who ultimately own or control 25% or more of the entity must be declared. If no individual meets this threshold, declare the senior managing official. Failure to disclose UBOs may result in application rejection.' :'Declare any person acting on behalf of or with beneficial interest in this investment. If investing on behalf of a third party, full KYC must be completed for that party.'}
              </p>
            </div>
          </div>

          {/* UBO entries */}
          <div className="space-y-4">
            {ubos.map((ubo, i) => (
              <UBORow key={ubo.id} ubo={ubo} index={i} onChange={handleUBOChange} onRemove={handleRemoveUBO} />
            ))}
          </div>

          <button onClick={handleAddUBO} className="btn-secondary text-sm gap-2 w-full justify-center">
            <Plus size={14} /> Add Another UBO
          </button>

          {/* Ownership summary */}
          {ubos.some(u => u.ownership) && (
            <div className="glass-card rounded-xl p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Ownership Summary</p>
              <div className="space-y-2">
                {ubos.filter(u => u.name && u.ownership).map(u => (
                  <div key={u.id} className="flex items-center gap-3">
                    <div className="flex-1 bg-surface-3 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.min(parseFloat(u.ownership) || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-foreground font-mono-data w-10 text-right">{u.ownership}%</span>
                    <span className="text-xs text-muted-foreground w-40 truncate">{u.name}</span>
                    {u.isPEP && <Flag size={12} className="text-warning flex-shrink-0" />}
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-border mt-2">
                  <span className="text-xs text-muted-foreground">Total declared</span>
                  <span className={`text-xs font-bold font-mono-data ${
                    ubos.reduce((s, u) => s + (parseFloat(u.ownership) || 0), 0) >= 100 ? 'text-positive' : 'text-warning'
                  }`}>
                    {ubos.reduce((s, u) => s + (parseFloat(u.ownership) || 0), 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step: Compliance Review Queue ────────────────────────────────────── */}
      {currentStep === 'review' && (
        <div className="space-y-5">
          <SectionHeader title="Compliance Review Queue Assignment" subtitle="Assign this application to the appropriate review queue and compliance officer" />

          {/* Application summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Documents" value={`${uploadedDocs.filter(d => d.status === 'verified').length}/${uploadedDocs.length}`} subValue="Verified / Total" variant="info" icon={FileText} />
            <MetricCard label="Liveness" value={livenessStatus === 'passed' ? 'Passed' : 'Pending'} subValue="Biometric check" variant={livenessStatus === 'passed' ? 'positive' : 'warning'} icon={Camera} />
            <MetricCard label="UBOs Declared" value={`${ubos.filter(u => u.name).length}`} subValue={`${ubos.filter(u => u.isPEP).length} PEP flagged`} variant={ubos.some(u => u.isPEP) ? 'warning' : 'default'} icon={Users} />
            <MetricCard label="App Type" value={appType === 'individual' ? 'KYC' : 'KYB'} subValue={appType === 'individual' ? 'Individual' : 'Corporate entity'} variant="default" icon={Shield} />
          </div>

          {/* Risk classification */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm font-semibold text-foreground">Risk Classification</p>
            <div className="flex flex-wrap gap-2">
              {(['low', 'medium', 'high', 'critical'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setRiskLevel(level)}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold capitalize transition-all ${
                    riskLevel === level
                      ? level === 'low' ? 'border-positive bg-positive/10 text-positive'
                        : level === 'medium' ? 'border-warning bg-warning/10 text-warning'
                        : level === 'high'? 'border-negative bg-negative/10 text-negative' :'border-critical bg-critical/10 text-critical' :'border-border bg-surface-3 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {riskLevel === 'low' && 'Standard KYC process. No enhanced measures required. Eligible for automated approval pathway.'}
              {riskLevel === 'medium' && 'Standard review with additional source-of-funds verification. Manual officer review required.'}
              {riskLevel === 'high' && 'Enhanced Due Diligence (EDD) required. Senior compliance officer review. Extended SLA applies.'}
              {riskLevel === 'critical' && 'Immediate escalation required. Application placed on compliance hold pending senior management review. Potential STR filing.'}
            </p>
          </div>

          {/* Queue selection */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm font-semibold text-foreground">Review Queue</p>
            <div className="space-y-2">
              {REVIEW_QUEUES.map(queue => (
                <label
                  key={queue.id}
                  className={`flex items-center gap-4 p-3 rounded-xl border cursor-pointer transition-all ${
                    selectedQueue === queue.id
                      ? 'border-primary bg-primary/5' :'border-border bg-surface-3 hover:border-border/80'
                  }`}
                >
                  <input
                    type="radio"
                    name="queue"
                    value={queue.id}
                    checked={selectedQueue === queue.id}
                    onChange={() => setSelectedQueue(queue.id)}
                    className="accent-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{queue.label}</p>
                    <p className="text-xs text-muted-foreground">SLA: {queue.sla}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-mono-data text-muted-foreground">{queue.count} in queue</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Officer assignment */}
          <div className="glass-card rounded-xl p-5 space-y-3">
            <p className="text-sm font-semibold text-foreground">Assign Compliance Officer</p>
            <select
              value={selectedOfficer}
              onChange={(e) => setSelectedOfficer(e.target.value)}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
            >
              {COMPLIANCE_OFFICERS.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Review notes */}
          <div className="glass-card rounded-xl p-5 space-y-3">
            <p className="text-sm font-semibold text-foreground">Intake Notes</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any relevant notes for the reviewing officer — flags, observations, special instructions…"
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors resize-none"
            />
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield size={13} className="text-primary" />
              <span>Submission is logged to the immutable audit trail with your user ID, timestamp and IP address.</span>
            </div>
            <button onClick={handleSubmit} className="btn-primary text-sm gap-2 flex-shrink-0">
              <ClipboardCheck size={14} />
              Submit to Compliance Queue
            </button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <button
          onClick={() => setCurrentStep(STEPS[Math.max(0, stepIndex - 1)].id)}
          disabled={stepIndex === 0}
          className="btn-secondary text-sm gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ← Previous
        </button>
        <div className="flex items-center gap-1">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`w-2 h-2 rounded-full transition-all ${
                i === stepIndex ? 'bg-primary w-4' : i < stepIndex ? 'bg-positive' : 'bg-surface-4'
              }`}
            />
          ))}
        </div>
        {stepIndex < STEPS.length - 1 ? (
          <button
            onClick={() => setCurrentStep(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)].id)}
            className="btn-primary text-sm gap-2"
          >
            Next Step <ArrowRight size={14} />
          </button>
        ) : (
          <button onClick={handleSubmit} className="btn-primary text-sm gap-2">
            <ClipboardCheck size={14} /> Submit Application
          </button>
        )}
      </div>
    </div>
  );
}
